import { useState, useEffect, useRef, useCallback } from 'react';
import { voiceService } from '../services/voiceService';
import apiService from '../services/api';

export const useVoiceAssistant = (token) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [messages, setMessages] = useState([]);
  const [emotion, setEmotion] = useState('neutral');
  const [visualData, setVisualData] = useState(null);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  
  const serviceRef = useRef(null);
  const contextLoadedRef = useRef(false);  // Track if context is loaded
  const contextDataRef = useRef(null);     // Store context data
  const contextRetryTimeoutRef = useRef(null);
  const contextStatusRef = useRef('idle');

  const handleMessage = useCallback((data) => {
    // Handle thinking indicator
    if (data.type === 'thinking') {
      setIsTyping(true);
      setStreamingMessage('');
      return;
    }

    // Handle streaming chunks
    if (data.type === 'stream_chunk') {
      setIsTyping(true);
      setStreamingMessage(data.full_text || '');

      // Add or update the streaming message in the messages array
      setMessages(prev => {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage && lastMessage.streaming) {
          // Update existing streaming message
          return [...prev.slice(0, -1), {
            ...lastMessage,
            text: data.full_text || ''
          }];
        } else {
          // Add new streaming message
          return [...prev, {
            id: Date.now(),
            sender: 'assistant',
            text: data.full_text || '',
            timestamp: new Date().toLocaleTimeString(),
            streaming: true
          }];
        }
      });

      return;
    }

    // Handle stream completion
    if (data.type === 'stream_complete') {
      setIsTyping(false);

      // Don't add a new message, just update the streaming one to be final
      if (streamingMessage) {
        setMessages(prev => {
          // Check if we already have the streaming message
          const lastMessage = prev[prev.length - 1];
          if (lastMessage && lastMessage.streaming) {
            // Update the last message to be non-streaming
            return [...prev.slice(0, -1), {
              ...lastMessage,
              streaming: false
            }];
          }
          // If no streaming message found, add the complete one
          return [...prev, {
            id: Date.now(),
            sender: 'assistant',
            text: data.full_text,
            timestamp: new Date().toLocaleTimeString(),
            streaming: false
          }];
        });
      }

      setStreamingMessage('');

      if (data.emotion) {
        setEmotion(data.emotion);
      }

      return;
    }

    // Handle regular responses
    if (data.type === 'response' || data.type === 'message') {
      setIsTyping(false);
      setStreamingMessage('');

      setMessages(prev => [...prev, {
        id: Date.now(),
        sender: data.sender || 'assistant',
        text: data.text,
        data: data.data,
        timestamp: new Date().toLocaleTimeString()
      }]);

      if (data.emotion) {
        setEmotion(data.emotion);
      }

      if (data.audio) {
        serviceRef.current.playAudio(data.audio);
      }
    }

    // Handle separate audio messages (async TTS)
    if (data.type === 'audio' && data.audio) {
      serviceRef.current.playAudio(data.audio);
    }

    // Handle streaming audio
    if (data.type === 'audio_start') {
      serviceRef.current.startAudioStream();
    }

    if (data.type === 'audio_chunk' && data.audio_chunk) {
      serviceRef.current.addAudioChunk(data.audio_chunk);
    }

    if (data.type === 'audio_complete') {
      serviceRef.current.completeAudioStream();
    }

    if (data.type === 'audio_error') {
      serviceRef.current.handleAudioError();
    }

    // Handle sentence-based TTS audio for real-time playback
    if (data.type === 'tts_sentence') {
      console.log(`🎵 Queueing sentence ${data.sentence_index} audio...`);
      serviceRef.current.queueSentenceAudio(data.audio, data.format, data.sentence_index);
    }

    // Handle TTS audio (pyttsx3 offline voice)
    if (data.type === 'tts_audio') {
      console.log('🎵 Playing TTS audio (offline voice)...');
      serviceRef.current.playTTSAudio(data.audio, data.format);
    }

    if (data.type === 'tts_complete') {
      console.log('✅ TTS streaming complete');
      serviceRef.current.processSentenceQueue();
    }

    // Legacy Edge TTS support (backwards compatibility)
    if (data.type === 'edge_tts_audio') {
      console.log('🎵 Playing TTS audio...');
      serviceRef.current.playTTSAudio(data.audio, data.format || 'mp3');
    }

    if (data.type === 'edge_tts_complete') {
      console.log('✅ TTS complete');
    }

    // Handle fallback TTS (if Edge TTS fails)
    if (data.type === 'browser_tts') {
      // For parallel TTS, start immediately without waiting
      if (data.is_parallel) {
        console.log('🎵 Starting parallel voice synthesis...');
      }
      serviceRef.current.playBrowserTTS(data.text, data.voice_settings);
    }

    if (data.visualization) {
      setVisualData(data);
    }
  }, [streamingMessage]);

  const sendContextToAssistant = useCallback(() => {
    if (!serviceRef.current || serviceRef.current.ws?.readyState !== WebSocket.OPEN) {
      return false;
    }

    if (!contextLoadedRef.current || contextStatusRef.current !== 'success' || !contextDataRef.current) {
      return false;
    }

    console.log('📤 Sending context data to assistant');
    serviceRef.current.send({
      type: 'context',
      ...contextDataRef.current
    });
    return true;
  }, []);

  const loadContextData = useCallback(async () => {
    if (!token || contextLoadedRef.current || contextStatusRef.current === 'loading') {
      return;
    }

    try {
      contextStatusRef.current = 'loading';
      console.log('📊 Loading assistant context data...');

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Context loading timeout')), 10000)
      );

      const dataPromise = Promise.all([
        apiService.getScenarios().catch((err) => {
          console.warn('⚠️ getScenarios failed:', err.message || err);
          return { data: [] };
        }),
        apiService.getFacilities().catch((err) => {
          console.warn('⚠️ getFacilities failed:', err.message || err);
          return { data: [] };
        }),
        apiService.getEmissionFactors().catch((err) => {
          console.warn('⚠️ getEmissionFactors failed:', err.message || err);
          return { data: null };
        })
      ]);

      const [scenariosRes, facilitiesRes, efRes] = await Promise.race([
        dataPromise,
        timeoutPromise
      ]);

      contextDataRef.current = {
        scenarios: scenariosRes?.data || [],
        facilities: facilitiesRes?.data || [],
        emission_factors: efRes?.data || null,
      };

      contextLoadedRef.current = true;
      contextStatusRef.current = 'success';
      console.log('✅ Context data loaded successfully');

      sendContextToAssistant();
    } catch (err) {
      contextStatusRef.current = 'error';
      console.warn('⚠️ Failed to load context data:', err.message);
      console.log('Voice assistant will work without context data');
      contextDataRef.current = {
        scenarios: [],
        facilities: [],
        emission_factors: null
      };

      if (contextRetryTimeoutRef.current) {
        clearTimeout(contextRetryTimeoutRef.current);
      }

      contextRetryTimeoutRef.current = setTimeout(() => {
        console.log('🔁 Retrying context load...');
        contextLoadedRef.current = false;
        contextStatusRef.current = 'idle';
        loadContextData();
      }, 5000);
    }
  }, [token, sendContextToAssistant]);

  // Load context data ONCE on mount, independent of WebSocket connection
  useEffect(() => {
    loadContextData();
    return () => {
      if (contextRetryTimeoutRef.current) {
        clearTimeout(contextRetryTimeoutRef.current);
      }
    };
  }, [loadContextData]);

  useEffect(() => {
    // Only initialize if we have a token and don't already have a service
    if (!token || serviceRef.current) {
      return;
    }

    console.log('🎤 Initializing Voice Assistant service...');
    
    // Initialize voice service
    serviceRef.current = new voiceService(token);
    
    // Set up event listeners
    serviceRef.current.on('connected', () => {
      console.log('🎤 Voice Assistant hook: Connected');
      setIsConnected(true);
      
      // Send context if it's already loaded
      if (contextLoadedRef.current && contextDataRef.current) {
        console.log('📤 Sending context data to assistant');
        serviceRef.current.send({
          type: 'context',
          ...contextDataRef.current
        });
      }
    });
    
    serviceRef.current.on('disconnected', () => {
      console.log('🎤 Voice Assistant hook: Disconnected');
      setIsConnected(false);
    });
    
    serviceRef.current.on('message', handleMessage);
    serviceRef.current.on('speaking', (speaking) => setIsSpeaking(speaking));
    serviceRef.current.on('listening', (listening) => setIsListening(listening));
    serviceRef.current.on('interim_transcript', (transcript) => setInterimTranscript(transcript));
    
    serviceRef.current.on('error', (error) => {
      console.error('🎤 Voice service error:', error);
      // Handle specific error types
      if (error.type === 'auth') {
        console.error('🔐 Authentication error - Voice Assistant disabled');
      }
    });
    
    serviceRef.current.on('user_message', (messageData) => {
      // Immediately add user's voice message to the chat
      setMessages(prev => [...prev, {
        id: Date.now(),
        sender: 'user',
        text: messageData.text,
        timestamp: messageData.timestamp,
        source: messageData.source
      }]);
    });
    
    // Connect with a small delay to ensure everything is set up
    setTimeout(() => {
      if (serviceRef.current && token) {
        serviceRef.current.connect();
      }
    }, 100);
    
    return () => {
      if (serviceRef.current) {
        console.log('🎤 Cleaning up Voice Assistant service...');
        serviceRef.current.disconnect();
        serviceRef.current = null;
      }
    };
  }, [token]);

  const sendMessage = useCallback((message) => {
    if (!serviceRef.current) {
      console.warn('⚠️ Voice service not initialized');
      return;
    }

    // Check WebSocket state directly instead of relying on isConnected state
    const wsReady = serviceRef.current.ws?.readyState === WebSocket.OPEN;

    if (!wsReady) {
      console.warn('⚠️ WebSocket not ready, cannot send message');
      console.log('Current readyState:', serviceRef.current.ws?.readyState);
      // Optionally show error to user
      setMessages(prev => [...prev, {
        id: Date.now(),
        sender: 'system',
        text: 'Connection lost. Reconnecting...',
        timestamp: new Date().toLocaleTimeString(),
        error: true
      }]);
      return;
    }
    
    // Include short conversation history for context (last 10 turns)
    const history = messages.slice(-20).map(m => ({
      role: m.sender === 'user' ? 'user' : 'assistant',
      content: m.text || ''
    }));

    // Encourage structured, numeric responses from the assistant
    const preferences = {
      structured: true,
      prefer_numbers: true,
      number_format: { style: 'decimal', minFractionDigits: 0, maxFractionDigits: 2 },
      units_default: 'kg CO₂e',
      layout: 'table_or_bullets',
      format: 'json_or_table',
      force_json_preferred: true,
      fields_suggested: ['scenario_name','facility','created_at','total_emissions','stage_totals']
    };

    try {
      serviceRef.current.send({
        ...message,
        history,
        preferences
      });
      
      if (message.type === 'text') {
        setMessages(prev => [...prev, {
          id: Date.now(),
          sender: 'user',
          text: message.text,
          timestamp: new Date().toLocaleTimeString()
        }]);
      }
      
      console.log('✅ Message sent successfully');
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      setMessages(prev => [...prev, {
        id: Date.now(),
        sender: 'system',
        text: 'Failed to send message. Please try again.',
        timestamp: new Date().toLocaleTimeString(),
        error: true
      }]);
    }
  }, [messages]);

  const startListening = useCallback(async () => {
    if (serviceRef.current) {
      setInterimTranscript(''); // Clear any previous interim transcript
      const started = await serviceRef.current.startListening();
      // Note: setIsListening is now handled by the event listener
      return started;
    }
    return false;
  }, []);

  const stopListening = useCallback(() => {
    if (serviceRef.current) {
      serviceRef.current.stopListening();
      setInterimTranscript(''); // Clear interim transcript
      // Note: setIsListening is now handled by the event listener
    }
  }, []);

  const stopAllAudio = useCallback(() => {
    if (serviceRef.current) {
      serviceRef.current.stopAllAudio();
    }
  }, []);

  return {
    isConnected,
    isListening,
    isSpeaking,
    messages,
    emotion,
    visualData,
    interimTranscript,
    isTyping,
    streamingMessage,
    sendMessage,
    startListening,
    stopListening,
    stopAllAudio
  };
};

export default useVoiceAssistant;
